/**
 * Sentry configuration for the admin dashboard.
 *
 * Deliberately near-identical to apps/customer-rfq/src/config/sentry.js. The two
 * are not shared: CRA's ModuleScopePlugin blocks imports from outside src/, and
 * this repo already duplicates per-app clients rather than fighting three build
 * systems for forty lines of config. Keep them in step by hand.
 *
 * REACT_APP_SENTRY_DSN is injected per site by scripts/deploy.js, so this app's
 * value is the admin Sentry project's DSN, not the customer app's. See
 * docs/technical/error-monitoring-plan.md §3.1 — one project per app.
 *
 * A build with no DSN initialises nothing (see src/index.js). That is the normal
 * state locally.
 */

/** Public by design — a DSN is readable in any bundle that ships it. */
const dsn = process.env.REACT_APP_SENTRY_DSN || '';

/**
 * `dev` | `test` | `production`. NOT NODE_ENV, which CRA sets to "production"
 * for every build including the dev and test deployments.
 */
const environment = process.env.REACT_APP_SENTRY_ENV || 'development';

/**
 * The complete release string, built and injected by scripts/deploy.js, which
 * hands the identical value to `sentry-cli` for the source map upload. Reported
 * VERBATIM — do not reassemble it here.
 */
const release = process.env.REACT_APP_SENTRY_RELEASE || 'panelgear-admin@0.0.0-local';

/**
 * Route events through our own domain instead of straight to Sentry.
 *
 * Content blockers and Firefox's Enhanced Tracking Protection drop requests to
 * `*.ingest.sentry.io` before they leave the browser, so without this the errors
 * that go unreported are exactly the ones hitting the most privacy-conscious
 * users — a silent, self-selecting blind spot.
 *
 * Unset means no tunnel and the SDK talks to Sentry directly, which is the
 * correct behaviour until `sentry-tunnel` is deployed on a domain of ours.
 */
const tunnel = process.env.REACT_APP_SENTRY_TUNNEL || '';

const sentryConfig = {
  dsn,
  environment,
  ...(tunnel ? { tunnel } : {}),
  enabled: Boolean(dsn),

  release,

  debug: false,
  sendDefaultPii: false,

  tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

  /**
   * Replays only when something breaks. 50 a month on the free tier, and this
   * app shares that allowance with the customer app — see plan §3.2.
   */
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  /**
   * Empty on purpose. Appwrite rejects the `baggage` and `sentry-trace` headers,
   * so tracing a request to it fails CORS and the request never leaves the
   * browser — and Appwrite is the only backend this app talks to. The
   * azurewebsites pattern this replaced matched nothing in any app.
   */
  tracePropagationTargets: [],

  ignoreErrors: [
    /ResizeObserver loop/,
    'AbortError',
    'The operation was aborted',
    'TypeError: Failed to fetch',
    'TypeError: NetworkError when attempting to fetch resource.',
    'TypeError: cancelled',
    'Non-Error promise rejection captured',
    'top.GLOBALS',
    /^ResizeObserver/,
    'Java exception was raised during method invocation',
  ],

  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],

  /**
   * Drops events whose every frame lacks a real http(s) filename — injected
   * scripts. NOT keyed on `in_app`: source maps (plan §3.9) make library frames
   * not-in_app, and a genuine error inside a library must still be reported.
   */
  beforeSend(event) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (!frames?.length) return event;
    return frames.some((frame) => /^https?:\/\//.test(frame.filename || '')) ? event : null;
  },
};

export default sentryConfig;
